<template>
    <div class="app-shell" :style="contentOffsetStyle">
        <div
            v-if="showOfflineBanner"
            ref="offlineBanner"
            class="app-offline-banner alert alert-dismissible bg-primary d-flex align-items-center justify-content-center text-white mb-0 rounded-0 text-center py-3"
            role="alert"
        >
            <button
                type="button"
                class="btn-close btn-close-white"
                aria-label="Close"
                @click="dismissOfflineBanner"
            ></button>
            <div>
                <i class="fas fa-wifi-slash fa-beat fa-2x mb-2"></i>
                <span class="d-block">{{ __("It looks like your device is offline.") }}</span>
                <span class="d-block">{{ __("Please check your network connection and try again.") }}</span>
            </div>
        </div>
        <div
            v-else-if="showUpdateBanner"
            ref="updateBanner"
            class="app-update-banner alert alert-dismissible bg-dark d-flex align-items-center justify-content-center text-white mb-0 rounded-0 text-center py-3"
            role="alert"
        >
            <button
                type="button"
                class="btn-close btn-close-white"
                aria-label="Close"
                @click="dismissUpdateBanner"
            ></button>
            <div>
                <i class="fas fa-sync fa-2x mb-2"></i>
                <span class="d-block fw-bold">{{ __("A newer version is available.") }}</span>
                <span class="d-block mb-3">{{ __("Please refresh to get the latest updates.") }}</span>
                <button type="button" class="btn btn-light btn-sm px-4" @click="reloadForUpdate">
                    {{ __("Refresh now") }}
                </button>
            </div>
        </div>
        <slot />
    </div>
</template>

<script>
import {
    flushQueuedConnectivityLogs,
    logConnectivityChange,
    onConnectivityProbeChange,
    startBackgroundConnectivityMonitor,
    stopBackgroundConnectivityMonitor,
} from "../services/deviceConnectivityLog";
import { startAssetVersionMonitor, fetchAssetVersion, getStoredAssetVersion, storeAssetVersion } from "../services/assetVersionCheck";

export default {
    name: "AppShell",
    data() {
        return {
            isOffline: typeof navigator !== "undefined" ? !navigator.onLine : false,
            dismissed: false,
            updateAvailable: false,
            updateDismissed: false,
            bannerHeight: 0,
            lastLoggedConnectivity: typeof navigator !== "undefined" && navigator.onLine ? "online" : "offline",
        };
    },
    computed: {
        showOfflineBanner() {
            return this.isOffline && !this.dismissed;
        },
        showUpdateBanner() {
            return !this.showOfflineBanner && this.updateAvailable && !this.updateDismissed;
        },
        contentOffsetStyle() {
            if ((!this.showOfflineBanner && !this.showUpdateBanner) || !this.bannerHeight) {
                return {};
            }

            return {
                paddingTop: `${this.bannerHeight}px`,
            };
        },
    },
    watch: {
        showOfflineBanner(visible) {
            if (visible) {
                this.$nextTick(this.measureBanner);
            } else {
                this.$nextTick(this.measureBanner);
            }
        },
        showUpdateBanner(visible) {
            if (visible) {
                this.$nextTick(this.measureBanner);
            } else {
                this.$nextTick(this.measureBanner);
            }
        },
    },
    mounted() {
        window.addEventListener("online", this.syncOnlineStatus);
        window.addEventListener("offline", this.syncOnlineStatus);

        if (this.showOfflineBanner || this.showUpdateBanner) {
            this.$nextTick(this.measureBanner);
        }

        if (this.isOffline) {
            this.lastLoggedConnectivity = "offline";
            logConnectivityChange("offline").catch(() => {});
        } else {
            flushQueuedConnectivityLogs().catch(() => {});
        }

        this.unsubscribeProbe = onConnectivityProbeChange((reachable) => {
            this.applyConnectivityState(!reachable, false);
        });

        startBackgroundConnectivityMonitor();

        this.stopAssetVersionMonitor = startAssetVersionMonitor({
            onUpdate: () => {
                this.updateAvailable = true;
                this.updateDismissed = false;
                this.$nextTick(this.measureBanner);
            },
        });

        this.resizeObserver = new ResizeObserver(() => {
            if (this.showOfflineBanner || this.showUpdateBanner) {
                this.measureBanner();
            }
        });
    },
    beforeUnmount() {
        window.removeEventListener("online", this.syncOnlineStatus);
        window.removeEventListener("offline", this.syncOnlineStatus);
        stopBackgroundConnectivityMonitor();

        if (typeof this.unsubscribeProbe === 'function') {
            this.unsubscribeProbe();
        }

        if (typeof this.stopAssetVersionMonitor === 'function') {
            this.stopAssetVersionMonitor();
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        applyConnectivityState(offline, shouldLog = true) {
            const event = offline ? "offline" : "online";

            if (!offline) {
                this.dismissed = false;
            }

            this.isOffline = offline;

            if (shouldLog && event !== this.lastLoggedConnectivity) {
                this.lastLoggedConnectivity = event;
                logConnectivityChange(event).catch(() => {});
            }
        },
        syncOnlineStatus() {
            this.applyConnectivityState(!navigator.onLine, true);
        },
        dismissOfflineBanner() {
            this.dismissed = true;
            this.bannerHeight = 0;
            this.$nextTick(this.measureBanner);
        },
        dismissUpdateBanner() {
            this.updateDismissed = true;
            this.bannerHeight = 0;
            this.$nextTick(this.measureBanner);
        },
        async reloadForUpdate() {
            try {
                const previousVersion = getStoredAssetVersion();
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

                // Mark the new version as accepted before reload so the banner does not reappear.
                storeAssetVersion(version);

                await window.axios.post('/ajax/app-update-log', {
                    version: version,
                    previous_version: previousVersion,
                }, {
                    headers: csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {},
                });
            } catch (e) {
                // Still reload even if logging fails
            }

            window.location.reload();
        },
        measureBanner() {
            const banner = this.showOfflineBanner
                ? this.$refs.offlineBanner
                : this.showUpdateBanner
                    ? this.$refs.updateBanner
                    : null;

            if (!banner) {
                this.bannerHeight = 0;
                return;
            }

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver.observe(banner);
            }

            this.bannerHeight = banner.offsetHeight;
        },
    },
};
</script>

<style scoped>
.app-offline-banner,
.app-update-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1050;
    width: 100%;
}

.app-offline-banner .btn-close,
.app-update-banner .btn-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    opacity: 1;
}
</style>
